import { registerAuditLabels } from "@vixio/ui";
import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap, Tone } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo img (ver modules/img/README.md). */

export type StageKey = "critico" | "reativo" | "estruturado" | "evoluido";

export const STAGES: Array<{ key: StageKey; label: string; min: number; max: number; tone: Tone }> = [
  { key: "critico", label: "Crítico", min: 0, max: 39, tone: "crit" },
  { key: "reativo", label: "Reativo", min: 40, max: 59, tone: "warn" },
  { key: "estruturado", label: "Estruturado", min: 60, max: 79, tone: "info" },
  { key: "evoluido", label: "Evoluído", min: 80, max: 100, tone: "ok" },
];

export const STAGE_STATUS: StatusMap = Object.fromEntries(STAGES.map((s) => [s.key, { label: s.label, tone: s.tone }]));

/** Ordem e rótulos do instrumento v0 (a tela usa os do banco quando disponíveis). */
export const DIMENSIONS: Array<{ key: string; label: string }> = [
  { key: "delegacao", label: "Delegação" },
  { key: "feedback", label: "Feedback e Comunicação" },
  { key: "rotina", label: "Rotina de Gestão" },
  { key: "indicadores", label: "Indicadores e Decisão por Dados" },
  { key: "pessoas", label: "Desenvolvimento de Pessoas" },
];

export const dimensionLabel = (key: string | null | undefined): string => DIMENSIONS.find((d) => d.key === key)?.label ?? key ?? "";

export interface ImgContext {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  referrer?: string;
  pillar?: string;
  team_size?: string;
  [k: string]: unknown;
}

export interface ImgResult {
  id: string;
  org_id: string;
  response_id: string;
  person_id: string | null;
  person_name: string | null;
  person_email: string | null;
  person_phone: string | null;
  score: number;
  stage: StageKey;
  weakest_dimension: string;
  dimension_scores: Array<{ key: string; label: string; score: number | null }>;
  feedback: string;
  context: ImgContext;
  created_at: string;
}

export interface FeedbackTemplate {
  id: string;
  org_id: string;
  kind: "stage" | "dimension" | "closing";
  key: string;
  text: string;
  updated_at: string;
  updated_by: string | null;
}

export interface Instrument {
  version: string;
  status: string;
  note: string | null;
  definition: {
    title: string;
    intro: string;
    disclaimer?: string;
    status?: string;
    scale: { min: number; max: number; labels: string[] };
    dimensions: Array<{ key: string; label: string; description?: string }>;
    items: Array<{ id: string; text: string; dimension: string }>;
  };
}

export function useInstrument() {
  return useQuery({
    queryKey: ["img", "instrument"],
    staleTime: 10 * 60_000,
    queryFn: async () =>
      (await unwrap<Instrument[]>(from("img", "instrument_versions").select("version, status, note, definition").order("created_at", { ascending: false }).limit(1)))[0] ??
      null,
  });
}

/** Formulário `img` no forms (existe depois de img.install). */
export function useImgForm(orgId: string) {
  return useQuery({
    queryKey: ["img", "form", orgId],
    queryFn: async () =>
      (await unwrap<Array<{ id: string; status: string; min_group_size: number }>>(
        from("forms", "forms").select("id, status, min_group_size").eq("org_id", orgId).eq("slug", "img").limit(1),
      ))[0] ?? null,
  });
}

/** Envio da devolutiva (img 0004): automático depois do resultado ou reenvio pela equipe. */
export interface Devolutiva {
  id: string;
  org_id: string;
  result_id: string;
  person_id: string | null;
  trigger: "auto" | "resend";
  status: "requested" | "skipped";
  reason: string | null;
  channel: "email" | "whatsapp" | null;
  document_id: string | null;
  document_code: string | null;
  created_at: string;
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "img.install": { args: { p_org: string }; returns: string };
    "img.resend_devolutiva": {
      args: { p_result: string };
      returns: { devolutiva_id: string; status: "requested" | "skipped"; reason?: string; channel?: string; document_code?: string | null };
    };
    "img.set_feedback_template": { args: { p_org: string; p_kind: FeedbackTemplate["kind"]; p_key: string; p_text: string }; returns: string };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "img.installed": "IMG instalado",
  "img.feedback_template_set": "Modelo da devolutiva alterado",
});
